<template>
  <div class="paypal-card-checkout">
    <!-- 卡片字段容器 -->
    <div class="card-fields-container">
      <div class="card-field" ref="cardNumber"></div>
      <div class="flex-container">
        <div class="card-field" ref="cardExpiry"></div>
        <div class="card-field" ref="cardCvv"></div>
      </div>
      <Input type="text" v-model="cardholderName" placeholder="Name on the card" @on-blur="validateName"></Input>
    </div>
    <!-- 错误提示 -->
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </div>
</template>

<script>
import { paypalAddOrder, paypalCheckoutOrder, getPaypalToken } from '@/service/vbef'
export default {
  name: 'PayPalCardCheckout',
  data() {
    return {
      sdkInstance: null, // 保存 SDK 实例
      cardSession: null, // 保存卡片会话实例
      isCardEligible: false, // 是否支持卡片支付
      errorMessage: '', // 错误提示
      cardholderName: '', // 持卡人姓名
    }
  },
  mounted() {
    // 挂载后加载 PayPal SDK
    this.loadPayPalSdk()
  },
  beforeDestroy() {
    // 组件销毁前清理资源
    if (this.cardSession) {
      this.cardSession = null
    }
    if (this.sdkInstance) {
      this.sdkInstance = null
    }
    // 移除已加载的 SDK 脚本，避免重复加载
    const sdkScript = document.querySelector('script[src*="paypal.com/web-sdk/v6/core"]')
    if (sdkScript) {
      sdkScript.remove()
    }
  },
  methods: {
    /**
     * 验证持卡人姓名
     */
    validateName() {
      // 去除首尾空格
      const trimmedName = this.cardholderName.trim()

      // 验证是否为空（必填校验）
      if (!trimmedName) {
        this.errorMessage = 'Please enter the cardholder name'
        return false
      }

      // 验证通过，清空错误提示
      this.errorMessage = ''
      return true
    },
    /**
     * 加载 PayPal SDK 脚本
     */
    loadPayPalSdk() {
      // 检查是否已加载 SDK，避免重复加载
      if (window.paypal) {
        this.initPayPalSdk()
        return
      }
      // 创建 SDK 脚本标签
      const script = document.createElement('script')
      script.src = this.$store.state.paypalScript
      script.async = true
      script.onload = () => this.initPayPalSdk()
      script.onerror = () => {
        this.errorMessage =
          'PayPal payment component failed to load, please refresh the page and try again'
      }
      document.body.appendChild(script)
    },
    /**
     * 初始化 PayPal SDK，并检查卡片支付是否可用
     */
    async initPayPalSdk() {
      try {
        // 获取浏览器安全客户端令牌
        const clientToken = await this.getBrowserSafeClientToken()
        this.sdkInstance = await window.paypal.createInstance({
          clientToken,
          components: ['card-fields'],
        })
        const paymentMethods = await this.sdkInstance.findEligibleMethods()
        this.isCardEligible = paymentMethods.isEligible('advanced_cards')
        if (this.isCardEligible) {
          this.renderCardFields()
        } else {
          this.errorMessage =
            'Card payment is not supported currently, please choose another payment method'
        }
      } catch (err) {
        console.error('SDK 初始化失败:', err)
        this.errorMessage = 'Payment component initialization failed, please try again later'
      }
    },
    /**
     * 渲染卡片字段
     */
    renderCardFields() {
      this.cardSession = this.sdkInstance.createCardFieldsOneTimePaymentSession()
      const styleParams = {
        input: {
          fontSize: '16px',
          fontWeight: '500',
          fontFamily: 'Roboto, Roboto',
          lineHeight: '1.5',
          padding: '8px 16px',
          border: '1px solid #D1D5DB',
          borderRadius: '4px',
          color: '#374151',
          background: '#FFFFFF',
          transition: 'all 0.2s ease-in-out',
        },
        'input::placeholder': {
          color: '#9CA3AF',
        },
        '.invalid': {
          border: '1px solid #F44040',
        },
        '.invalid.focus': {
          border: '1px solid #F44040',
        },
        'input.focus': {
          outline: 'none',
          border: '1px solid #4B5563',
        },
      }
      const numberField = this.cardSession.createCardFieldsComponent({
        type: 'number',
        placeholder: 'Card Number',
        style: styleParams,
      })
      const expiryField = this.cardSession.createCardFieldsComponent({
        type: 'expiry',
        placeholder: 'MM / YY',
        style: styleParams,
      })
      const cvvField = this.cardSession.createCardFieldsComponent({
        type: 'cvv',
        placeholder: 'CVV',
        style: styleParams,
      })
      this.$refs.cardNumber.appendChild(numberField)
      this.$refs.cardExpiry.appendChild(expiryField)
      this.$refs.cardCvv.appendChild(cvvField)
    },
    /**
     * 处理支付事件，创建订单并提交支付信息
     */
    async handlePay(orderParams) {
      if (!this.cardSession) {
        this.errorMessage = 'Payment component is not initialized, please refresh the page'
        this.$emit('updateLoading', false)
        return
      }

      if (!this.validateName()) {
        this.$emit('updateLoading', false)
        return
      }

      try {
        this.errorMessage = ''

        // 创建订单
        const { activityOrderNumber, paypalOrderId } = await this.createOrder(orderParams)
        // 提交卡片信息和订单
        const { data, state } = await this.cardSession.submit(paypalOrderId, {
          billingAddress: {
            fullName: this.cardholderName.trim(),
          },
        })
        
        // 处理支付状态
        switch (state) {
          case 'succeeded':
            const captureResult = await this.captureOrder(activityOrderNumber)
            if (captureResult.code == 0 && captureResult.data.status == 2) {
              this.$router.push({
                path: `/ticket-purchased/${captureResult.data.activityTicketUserRelationId}`,
              })
            } else {
              this.errorMessage = captureResult.enMsg || captureResult.msg
            }
            break
          case 'canceled':
            this.errorMessage = 'You canceled the payment verification, please try again'
            break
          case 'failed':
            this.errorMessage = 'Payment failed, please check your bank card information and try again'
            console.warn('支付失败:', state, data)
            break
          default:
            this.errorMessage = 'Payment status is abnormal, please contact customer service'
            console.warn('未处理的状态:', state, data)
        }
      } catch (err) {
        console.error('支付失败:', err)
        this.errorMessage = 'An error occurred during payment, please try again later'
      } finally {
        this.$emit('updateLoading', false)
      }
    },
    /**
     * 获取浏览器安全令牌
     */
    async getBrowserSafeClientToken() {
      try {
        const res = await getPaypalToken()
        return res.code === 0 ? res.data.token : ''
      } catch (err) {
        throw new Error('获取支付令牌失败: ' + (err.message || '网络错误'))
      }
    },
    /**
     * 创建订单
     */
    async createOrder(orderParams) {
      try {
        const res = await paypalAddOrder(orderParams)
        if (res.code === 0) {
          return res.data
        } else {
          this.errorMessage = 'An error occurred during payment, please try again later'
        }
      } catch (err) {
        throw new Error('创建订单失败: ' + (err.message || '网络错误'))
      }
    },
    /**
     * 捕获订单
     */
    async captureOrder(orderId) {
      try {
        const res = await paypalCheckoutOrder({
          activityOrderNumber: orderId,
        })
        return res
      } catch (err) {
        throw new Error('确认支付失败: ' + (err.message || '网络错误'))
      }
    },
  },
}
</script>
<style lang="stylus" scoped>
.error-message
  color #ED4014
  padding-top 6px
  font-size 14px
.card-fields-container
  display flex
  flex-direction column
  gap 20px
  .card-field
    height 50px
  >>> .ivu-input
    font-weight 500
    font-family Roboto, Roboto
    height 50px
    border 1px solid #D1D5DB
    border-radius 4px
    font-size 16px
    padding 8px 16px
    color #374151
    background #FFFFFF
    transition all 0.2s ease-in-out
    &:focus
      outline none
      border-color #4B5563
    &::placeholder
      color #9CA3AF
.flex-container
  display flex
  gap 16px
  .card-field
    flex 1
</style>
